import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const pageStyles = {
  body: {
    background: '#0b1220',
    color: '#e5e7eb',
    fontFamily: '"Noto Sans KR", system-ui, -apple-system, Segoe UI, Roboto',
    minHeight: '100vh'
  },
  topbar: {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    background: 'rgba(11,18,32,.92)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255,255,255,.08)'
  },
  container: {
    maxWidth: '1600px',
    margin: '0 auto',
    paddingLeft: '12px',
    paddingRight: '12px'
  },
  topInner: {
    paddingTop: '16px',
    paddingBottom: '16px'
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '8px'
  },
  brand: {
    fontWeight: 900,
    letterSpacing: '-0.3px'
  },
  sub: {
    color: '#9ca3af',
    fontSize: '13px'
  },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '999px',
    border: '1px solid rgba(255,255,255,.10)',
    background: 'rgba(255,255,255,.06)',
    color: '#e5e7eb',
    fontSize: '13px',
    cursor: 'pointer',
    userSelect: 'none'
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#ef4444',
    boxShadow: '0 0 0 3px rgba(239,68,68,.12)'
  },
  dotOn: {
    background: '#22c55e',
    boxShadow: '0 0 0 3px rgba(34,197,94,.14)'
  },
  filterWrap: {
    marginTop: '12px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  filterBtn: {
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.06)',
    color: '#e5e7eb',
    padding: '7px 12px',
    borderRadius: '999px',
    fontWeight: 800,
    letterSpacing: '-.2px',
    cursor: 'pointer'
  },
  filterBtnActive: {
    background: '#2563eb',
    borderColor: '#2563eb',
    color: '#fff'
  },
  ordersWrap: {
    padding: '18px 0 28px',
    display: 'grid',
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    gap: '22px',
    alignItems: 'stretch'
  },
  empty: {
    textAlign: 'center',
    color: '#9ca3af',
    padding: '3rem 0'
  }
};

function getGridColumns() {
  const w = window.innerWidth;
  if (w >= 1200) return 'repeat(5, minmax(0, 1fr))';
  if (w >= 992) return 'repeat(4, minmax(0, 1fr))';
  if (w >= 768) return 'repeat(3, minmax(0, 1fr))';
  if (w >= 576) return 'repeat(2, minmax(0, 1fr))';
  return 'repeat(1, minmax(0, 1fr))';
}

function simpleHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}

function formatDateTime(value) {
  if (!value) return '';

  const text = String(value).trim();

  // 백엔드에서 이미 07-15 14:31 형식으로 보낸 값은
  // 다시 Date로 변환하지 않아 시간대 보정을 막는다.
  if (/^\d{2}-\d{2} \d{2}:\d{2}$/.test(text)) {
    return text;
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return text;
  }

  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');

  return `${mm}-${dd} ${hh}:${mi}`;
}

function badgeBgClassToStyle(cls) {
  switch (cls) {
    case 'bg-dark':
      return { background: '#212529', color: '#fff' };
    case 'bg-warning text-dark':
      return { background: '#ffc107', color: '#212529' };
    case 'bg-info text-dark':
      return { background: '#0dcaf0', color: '#212529' };
    case 'bg-success':
      return { background: '#198754', color: '#fff' };
    case 'bg-danger':
      return { background: '#dc3545', color: '#fff' };
    case 'bg-primary':
      return { background: '#0d6efd', color: '#fff' };
    case 'bg-secondary':
      return { background: '#6c757d', color: '#fff' };
    case 'bg-light text-dark border':
      return {
        background: '#f8f9fa',
        color: '#212529',
        border: '1px solid rgba(255,255,255,.12)'
      };
    default:
      return { background: '#212529', color: '#fff' };
  }
}

function badgeBaseStyle(extra = {}) {
  return {
    border: '1px solid rgba(255,255,255,.12)',
    borderRadius: '999px',
    padding: '6px 10px',
    fontWeight: 900,
    letterSpacing: '-.2px',
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    ...extra
  };
}

function textSecondaryButtonStyle(disabled = false) {
  return {
    background: 'transparent',
    border: 'none',
    color: disabled ? 'rgba(156,163,175,.35)' : '#9ca3af',
    fontWeight: 800,
    padding: '8px 6px',
    lineHeight: 1,
    boxShadow: 'none',
    cursor: disabled ? 'default' : 'pointer'
  };
}

function blinkStyle(isNew) {
  return isNew ? { animation: 'kitchenBlink 1s infinite' } : {};
}

function ItemOptions({ options }) {
  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: '4px',
        paddingLeft: '8px',
        color: '#fbbf24',
        fontSize: '0.8rem',
        lineHeight: 1.45,
        whiteSpace: 'normal'
      }}
    >
      {options.map((option, index) => {
        const groupName = option?.option_group_name || '';
        const itemName = option?.option_item_name || '';
        const optionPrice = Number(option?.option_price || 0);

        return (
          <div key={`${option?.option_item_id || index}-${index}`}>
            · {groupName ? `${groupName}: ` : ''}{itemName || '옵션'}
            {optionPrice !== 0
              ? ` (${optionPrice > 0 ? '+' : ''}${optionPrice.toLocaleString()}원)`
              : ''}
          </div>
        );
      })}
    </div>
  );
}

function OrderCard({ o, setStatus }) {
  const orderCardStyle = {
    background: o.is_new ? 'rgba(59,130,246,.10)' : 'rgba(255,255,255,.06)',
    border: o.is_new
      ? '2px solid rgba(59,130,246,.45)'
      : '1px solid rgba(255,255,255,.10)',
    borderRadius: '20px',
    boxShadow: '0 12px 34px rgba(0,0,0,.40)',
    overflow: 'hidden',
    height: '100%'
  };

  const cardBodyStyle = {
    color: '#e5e7eb',
    padding: '18px 18px 14px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const mutedStyle = { color: '#9ca3af', fontSize: '0.875rem' };

  const actionWrapStyle = {
    marginTop: 'auto',
    paddingTop: '14px',
    paddingBottom: '14px',
    paddingRight: '14px',
    borderTop: '1px solid rgba(255,255,255,.10)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    flexWrap: 'wrap'
  };

  const disabled = ['DONE', 'CANCELLED'].includes(o.display_status);

  return (
    <div style={orderCardStyle}>
      <div style={cardBodyStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>#{o.id}</div>

            <span
              style={{
                ...badgeBaseStyle(badgeBgClassToStyle(o.badge_class)),
                ...blinkStyle(o.is_new)
              }}
            >
              {o.st_label}
            </span>

            <span style={badgeBaseStyle(badgeBgClassToStyle(o.ch_info.class))}>
              <span>{o.display_name}</span>
            </span>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: '1.25rem' }} />
          </div>
        </div>

        <div style={{ ...mutedStyle, marginTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>{o.short_dt}</span>

            {!o.is_table_order && o.display_phone ? (
              <>
                <span>·</span>
                <span>{o.display_phone}</span>
              </>
            ) : null}
          </div>

          {!o.is_table_order && o.pickup_short ? (
            <div style={{ marginTop: '4px' }}>
              <span style={{ color: '#0d6efd' }}>픽업 {o.pickup_short}</span>
            </div>
          ) : null}
        </div>

        {!o.is_table_order ? (
          <>
            {o.base_items && o.base_items.length > 0 ? (
              <div
                style={{
                  marginTop: '8px',
                  padding: '8px',
                  background: 'rgba(255,255,255,.04)',
                  border: '1px solid rgba(255,255,255,.08)',
                  borderRadius: '14px'
                }}
              >
                {o.base_items.map((it, idx) => (
                  <div
                    key={`${o.id}-base-${idx}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '10px',
                      fontSize: '0.875rem'
                    }}
                  >
                    <div style={{ maxWidth: '75%', overflow: 'hidden' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.name}
                        <span style={{ color: '#9ca3af' }}> x{it.qty}</span>
                      </div>
                      <ItemOptions options={it.options} />
                    </div>
                    <div style={{ fontFamily: pageStyles.mono.fontFamily }} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '8px', opacity: 0.75 }}>
                (첫주문 상세 없음)
              </div>
            )}
          </>
        ) : (
          <>
            {o.addons && o.addons.length > 0 ? (
              o.addons.map((ad, idx) => (
                <div
                  key={`${o.id}-addon-${ad.addon_id || idx}`}
                  style={{
                    marginTop: '8px',
                    padding: '8px',
                    background: 'rgba(34,197,94,.06)',
                    border: '1px solid rgba(34,197,94,.20)',
                    borderRadius: '14px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#86efac' }}>
                      주문{idx + 1}
                      <span style={{ color: 'rgba(156,163,175,.8)', fontWeight: 400 }}>
                        {' '}· {formatDateTime(ad.created_at)}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                      {ad.created_by ? ad.created_by : ''}
                    </div>
                  </div>

                  {ad.note ? (
                    <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '4px', opacity: 0.9 }}>
                      메모: {ad.note}
                    </div>
                  ) : null}

                  {ad.items && ad.items.length > 0 ? (
                    ad.items.map((it, jdx) => (
                      <div
                        key={`${o.id}-addon-item-${ad.addon_id || idx}-${jdx}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '10px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <div style={{ maxWidth: '75%', overflow: 'hidden' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {it.name}
                            <span style={{ color: '#9ca3af' }}> x{it.qty}</span>
                          </div>
                          <ItemOptions options={it.options} />
                        </div>
                        <div style={{ fontFamily: pageStyles.mono.fontFamily }} />
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '0.875rem', color: '#9ca3af', opacity: 0.8 }}>
                      (주문 상세 없음)
                    </div>
                  )}
                </div>
              ))
            ) : null}
          </>
        )}

        <div style={actionWrapStyle}>
          <button
            style={textSecondaryButtonStyle(disabled)}
            onClick={() => {
              if (!disabled) setStatus(o.id, 'COOKING');
            }}
            disabled={disabled}
          >
            준비중
          </button>

          <button
            style={textSecondaryButtonStyle(disabled)}
            onClick={() => {
              if (!disabled) setStatus(o.id, 'DONE');
            }}
            disabled={disabled}
          >
            완료
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KitchenOrders() {
  const [status, setStatus] = useState('ALL');
  const [shopId, setShopId] = useState(0);
  const [shopName, setShopName] = useState('');
  const [rows, setRows] = useState([]);
  const [gridCols, setGridCols] = useState(getGridColumns());
  const [soundEnabled, setSoundEnabled] = useState(false);

  const orderSoundRef = useRef(null);
  const inflightRef = useRef(false);
  const lastHashRef = useRef('');
  const lastLatestAddonIdRef = useRef(0);
  const firstLoadedRef = useRef(false);
  const wsRef = useRef(null);
  const wsRetryRef = useRef(0);
  const wsTimerRef = useRef(null);

  const searchStatus = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return (p.get('status') || 'ALL').trim();
  }, []);

  useEffect(() => {
    setStatus(searchStatus);
  }, [searchStatus]);

  useEffect(() => {
    const onResize = () => setGridCols(getGridColumns());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const audio = new Audio('/kitchen/sounds/order.mp3');
    audio.preload = 'auto';
    orderSoundRef.current = audio;
  }, []);

  const loadPageData = useCallback(async (statusArg) => {
    try {
      const res = await axios.get(`/api/kitchen/orders?status=${encodeURIComponent(statusArg)}`, {
        withCredentials: true
      });
      const data = res.data || {};
      if (!data.ok) {
        if (data.redirect) {
          window.location.href = data.redirect;
        }
        return;
      }
      setShopId(parseInt(data.shop_id || 0, 10));
      setShopName(data.shop_name || '');
    } catch (err) {
      if (err?.response?.status === 401) {
        const data = err.response.data || {};
        if (data.redirect) {
          window.location.href = data.redirect;
          return;
        }
      }
      console.log('loadPageData error', err);
    }
  }, []);

  const enableSound = useCallback(async () => {
    setSoundEnabled(true);

    try {
      const orderSound = orderSoundRef.current;
      if (!orderSound) return;
      const prevVol = orderSound.volume;
      orderSound.volume = 0.0;
      await orderSound.play();
      orderSound.pause();
      orderSound.currentTime = 0;
      orderSound.volume = prevVol;
    } catch (e) {}
  }, []);

  const playOrderVoice = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const orderSound = orderSoundRef.current;
      if (!orderSound) return;
      orderSound.pause();
      orderSound.currentTime = 0;
      orderSound.play();
    } catch (e) {}
  }, [soundEnabled]);

  const fetchOrders = useCallback(async (force = false, statusArg = status) => {
    if (inflightRef.current) return;
    inflightRef.current = true;

    const prevScrollY = window.scrollY;

    try {
      const res = await fetch(`/api/kitchen/orders/list?status=${encodeURIComponent(statusArg)}`, {
        cache: 'no-store',
        credentials: 'same-origin'
      });

      let data;
      try {
        data = await res.json();
      } catch (e) {
        console.log('fetchOrders json parse error', e);
        inflightRef.current = false;
        return;
      }

      if (!data.ok) {
        inflightRef.current = false;
        return;
      }

      const payloadStr = JSON.stringify(data);
      const h = simpleHash(payloadStr);

      if (!force && h === lastHashRef.current) {
        inflightRef.current = false;
        return;
      }

      lastHashRef.current = h;
      setRows(Array.isArray(data.rows) ? data.rows : []);

      const latestAddonId = parseInt(data.latest_addon_id || 0, 10);

      if (!firstLoadedRef.current) {
        firstLoadedRef.current = true;
        lastLatestAddonIdRef.current = latestAddonId;
      } else {
        if (latestAddonId > lastLatestAddonIdRef.current) {
          playOrderVoice();
          lastLatestAddonIdRef.current = latestAddonId;
        }
      }

      window.scrollTo({ top: prevScrollY, behavior: 'auto' });
    } catch (e) {
      console.log('fetchOrders error', e);
    } finally {
      inflightRef.current = false;
    }
  }, [playOrderVoice, status]);

  const setStatusApi = useCallback(async (orderId, st) => {
    if (!window.confirm(`주문 #${orderId} 상태를 ${st}(으)로 변경할까요?`)) return;

    const res = await fetch('/api/kitchen/order/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ order_id: orderId, current_status: st }),
      credentials: 'same-origin'
    });

    let data;
    try {
      data = await res.json();
    } catch (e) {
      const t = await res.text();
      alert('서버 응답이 JSON이 아닙니다:\n' + t);
      return;
    }

    if (!data.ok) {
      alert(data.msg || '상태 변경 실패');
      return;
    }

    fetchOrders(true, status);
  }, [fetchOrders, status]);

  useEffect(() => {
    loadPageData(status);
  }, [loadPageData, status]);

  useEffect(() => {
    fetchOrders(true, status);
    const timer = setInterval(() => fetchOrders(false, status), 3000);
    return () => clearInterval(timer);
  }, [fetchOrders, status]);

  useEffect(() => {
    if (!shopId || shopId <= 0) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';

    function wsConnect() {
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) {}
        wsRef.current = null;
      }

      wsRef.current = new WebSocket(`${proto}://${window.location.host}/ws/?shop_id=${shopId}`);

      wsRef.current.onopen = () => {
        console.log('WS 연결 성공 (kitchen)', shopId);
        wsRetryRef.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch (e) {
          return;
        }

        if (msg.type === 'NEW_ADDON') {
          fetchOrders(true, status);
        }
      };

      wsRef.current.onerror = (e) => console.log('WS 오류 (kitchen)', e);

      wsRef.current.onclose = (e) => {
        console.log('WS 종료 (kitchen)', e.code);
        const delay = Math.min(30000, 1000 * Math.pow(2, wsRetryRef.current++));
        clearTimeout(wsTimerRef.current);
        wsTimerRef.current = setTimeout(wsConnect, delay);
      };
    }

    wsConnect();

    return () => {
      clearTimeout(wsTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) {}
        wsRef.current = null;
      }
    };
  }, [shopId, fetchOrders, status]);

  const goStatus = (nextStatus) => {
    const url = nextStatus === 'ALL'
      ? '/kitchen/orders'
      : `/kitchen/orders?status=${encodeURIComponent(nextStatus)}`;
    window.history.replaceState({}, '', url);
    setStatus(nextStatus);
  };

  return (
    <div style={pageStyles.body}>
      <style>
        {`
          @keyframes kitchenBlink {
            0% { opacity: 1; }
            50% { opacity: .35; }
            100% { opacity: 1; }
          }
        `}
      </style>

      <div style={pageStyles.topbar}>
        <div style={{ ...pageStyles.container, ...pageStyles.topInner }}>
          <div style={pageStyles.topRow}>
            <div>
              <div style={pageStyles.brand}>주방 실시간 주문</div>
              <div style={pageStyles.sub}>
                <span style={{ fontWeight: 600 }}>{shopName}</span>
                <span style={{ margin: '0 4px', opacity: 0.35 }}>•</span>
                <span style={pageStyles.mono}>shop_id {shopId}</span>
              </div>
            </div>

            <div style={pageStyles.pill} onClick={enableSound}>
              <span
                style={{
                  ...pageStyles.dot,
                  ...(soundEnabled ? pageStyles.dotOn : {})
                }}
              />
              <span>{soundEnabled ? '소리 ON' : '소리 OFF'}</span>
            </div>
          </div>

          <div style={pageStyles.filterWrap}>
            {['ALL', 'NEW', 'COOKING', 'DONE', 'CANCELLED'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => goStatus(s)}
                style={{
                  ...pageStyles.filterBtn,
                  ...(status === s ? pageStyles.filterBtnActive : {})
                }}
              >
                {s === 'ALL' ? '전체' :
                 s === 'NEW' ? '새주문' :
                 s === 'COOKING' ? '준비중' :
                 s === 'DONE' ? '완료' : '취소'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={pageStyles.container}>
        {rows.length === 0 ? (
          <div style={pageStyles.empty}>주문이 없습니다.</div>
        ) : (
          <div style={{ ...pageStyles.ordersWrap, gridTemplateColumns: gridCols }}>
            {rows.map((o) => (
              <OrderCard key={o.id} o={o} setStatus={setStatusApi} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}